
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import type { ItineraryDay } from '@/app/lib/types';
import { ActivityCategorySchema } from '@/app/lib/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

type Activity = ItineraryDay['activities'][0];

interface ActivityEditorProps {
  activity?: Activity;
  onSave: (activity: Activity) => void;
  triggerButton: React.ReactNode;
}

const activitySchema = z.object({
  time: z.string().min(1, "Time is required."),
  locationName: z.string().min(1, "Location name is required."),
  description: z.string().optional(),
  category: ActivityCategorySchema,
  
  // Shared
  bookingReference: z.string().optional(),
  
  // Flight
  airline: z.string().optional(),
  flightNumber: z.string().optional(),
  departureAirport: z.string().optional(),
  arrivalAirport: z.string().optional(),
  departureTime: z.string().optional(),
  arrivalTime: z.string().optional(),

  // Train
  trainOperator: z.string().optional(),
  departureStation: z.string().optional(),
  arrivalStation: z.string().optional(),

  // Car Rental
  rentalCompany: z.string().optional(),
  pickupLocation: z.string().optional(),
  dropoffLocation: z.string().optional(),
  pickupTime: z.string().optional(),
  dropoffTime: z.string().optional(),

  // Lodging
  checkInTime: z.string().optional(),
  checkOutTime: z.string().optional(),
});

type ActivityFormData = z.infer<typeof activitySchema>;

export function ActivityEditor({ activity, onSave, triggerButton }: ActivityEditorProps) {
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<ActivityFormData>({
    resolver: zodResolver(activitySchema),
    defaultValues: activity || {
      time: 'Morning',
      category: 'other',
    },
  });

  const category = form.watch('category');

  const handleOpenChange = (open: boolean) => {
    if (open) {
      form.reset(activity || {
        time: 'Morning',
        locationName: '',
        description: '',
        category: 'other',
      });
    }
    setIsOpen(open);
  };
  
  const onSubmit = (data: ActivityFormData) => {
    const newActivityData: Activity = {
        ...(activity || {}),
        ...data,
        locationSlug: activity?.locationSlug || data.locationName.toLowerCase().replace(/\s+/g, '-'),
        coordinates: activity?.coordinates || { lat: 0, lng: 0 },
    };
    onSave(newActivityData);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{triggerButton}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{activity ? 'Edit Activity' : 'Add New Activity'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
                <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Category</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value} name={field.name}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a category" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {ActivityCategorySchema.options.map(option => (
                                        <SelectItem key={option} value={option}>
                                            {option.charAt(0).toUpperCase() + option.slice(1)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="locationName"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Location or Activity Name</FormLabel>
                            <FormControl>
                                <Input placeholder="e.g., Dores Beach, Flight to Inverness" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                 <FormField
                    control={form.control}
                    name="time"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Time of Day</FormLabel>
                            <FormControl>
                                <Input placeholder="e.g., Morning, 09:30" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                {/* Flight Fields */}
                {category === 'flight' && (
                    <div className="space-y-4 p-4 border rounded-md">
                        <h4 className="font-medium text-sm">Flight Details</h4>
                         <div className="grid grid-cols-2 gap-4">
                            <FormField control={form.control} name="airline" render={({ field }) => (<FormItem><FormLabel>Airline</FormLabel><FormControl><Input placeholder="e.g., British Airways" {...field} /></FormControl><FormMessage /></FormItem>)} />
                            <FormField control={form.control} name="flightNumber" render={({ field }) => (<FormItem><FormLabel>Flight No.</FormLabel><FormControl><Input placeholder="e.g., BA1442" {...field} /></FormControl><FormMessage /></FormItem>)} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <FormField control={form.control} name="departureAirport" render={({ field }) => (<FormItem><FormLabel>From</FormLabel><FormControl><Input placeholder="e.g., LHR" {...field} /></FormControl><FormMessage /></FormItem>)} />
                            <FormField control={form.control} name="arrivalAirport" render={({ field }) => (<FormItem><FormLabel>To</FormLabel><FormControl><Input placeholder="e.g., INV" {...field} /></FormControl><FormMessage /></FormItem>)} />
                        </div>
                         <div className="grid grid-cols-2 gap-4">
                            <FormField control={form.control} name="departureTime" render={({ field }) => (<FormItem><FormLabel>Departs</FormLabel><FormControl><Input placeholder="e.g., 09:40" {...field} /></FormControl><FormMessage /></FormItem>)} />
                            <FormField control={form.control} name="arrivalTime" render={({ field }) => (<FormItem><FormLabel>Arrives</FormLabel><FormControl><Input placeholder="e.g., 11:15" {...field} /></FormControl><FormMessage /></FormItem>)} />
                        </div>
                    </div>
                )}
                
                {/* Train Fields */}
                {category === 'train' && (
                     <div className="space-y-4 p-4 border rounded-md">
                        <h4 className="font-medium text-sm">Train Details</h4>
                        <FormField control={form.control} name="trainOperator" render={({ field }) => (<FormItem><FormLabel>Train Operator</FormLabel><FormControl><Input placeholder="e.g., ScotRail" {...field} /></FormControl><FormMessage /></FormItem>)} />
                        <div className="grid grid-cols-2 gap-4">
                            <FormField control={form.control} name="departureStation" render={({ field }) => (<FormItem><FormLabel>From</FormLabel><FormControl><Input placeholder="e.g., Glasgow Queen Street" {...field} /></FormControl><FormMessage /></FormItem>)} />
                            <FormField control={form.control} name="arrivalStation" render={({ field }) => (<FormItem><FormLabel>To</FormLabel><FormControl><Input placeholder="e.g., Fort William" {...field} /></FormControl><FormMessage /></FormItem>)} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <FormField control={form.control} name="departureTime" render={({ field }) => (<FormItem><FormLabel>Departs</FormLabel><FormControl><Input placeholder="e.g., 08:21" {...field} /></FormControl><FormMessage /></FormItem>)} />
                            <FormField control={form.control} name="arrivalTime" render={({ field }) => (<FormItem><FormLabel>Arrives</FormLabel><FormControl><Input placeholder="e.g., 12:08" {...field} /></FormControl><FormMessage /></FormItem>)} />
                        </div>
                    </div>
                )}

                {/* Car Rental Fields */}
                {category === 'car' && (
                     <div className="space-y-4 p-4 border rounded-md">
                        <h4 className="font-medium text-sm">Car Rental Details</h4>
                        <FormField control={form.control} name="rentalCompany" render={({ field }) => (<FormItem><FormLabel>Rental Company</FormLabel><FormControl><Input placeholder="e.g., Arnold Clark" {...field} /></FormControl><FormMessage /></FormItem>)} />
                         <div className="grid grid-cols-2 gap-4">
                            <FormField control={form.control} name="pickupLocation" render={({ field }) => (<FormItem><FormLabel>Pick-up</FormLabel><FormControl><Input placeholder="e.g., Inverness Airport" {...field} /></FormControl><FormMessage /></FormItem>)} />
                            <FormField control={form.control} name="dropoffLocation" render={({ field }) => (<FormItem><FormLabel>Drop-off</FormLabel><FormControl><Input placeholder="e.g., Edinburgh Airport" {...field} /></FormControl><FormMessage /></FormItem>)} />
                        </div>
                         <div className="grid grid-cols-2 gap-4">
                            <FormField control={form.control} name="pickupTime" render={({ field }) => (<FormItem><FormLabel>Pick-up Time</FormLabel><FormControl><Input placeholder="e.g., 12:00" {...field} /></FormControl><FormMessage /></FormItem>)} />
                            <FormField control={form.control} name="dropoffTime" render={({ field }) => (<FormItem><FormLabel>Drop-off Time</FormLabel><FormControl><Input placeholder="e.g., 10:00" {...field} /></FormControl><FormMessage /></FormItem>)} />
                        </div>
                    </div>
                )}

                {/* Lodging Fields */}
                {category === 'lodging' && (
                     <div className="space-y-4 p-4 border rounded-md">
                        <h4 className="font-medium text-sm">Lodging Details</h4>
                         <div className="grid grid-cols-2 gap-4">
                            <FormField control={form.control} name="checkInTime" render={({ field }) => (<FormItem><FormLabel>Check-in</FormLabel><FormControl><Input placeholder="e.g., 15:00" {...field} /></FormControl><FormMessage /></FormItem>)} />
                            <FormField control={form.control} name="checkOutTime" render={({ field }) => (<FormItem><FormLabel>Check-out</FormLabel><FormControl><Input placeholder="e.g., 11:00" {...field} /></FormControl><FormMessage /></FormItem>)} />
                        </div>
                    </div>
                )}

                {/* Shared Booking Ref */}
                {['flight', 'train', 'car', 'lodging'].includes(category) && (
                    <FormField control={form.control} name="bookingReference" render={({ field }) => (<FormItem><FormLabel>Booking Reference</FormLabel><FormControl><Input placeholder="e.g., 8X3Y7A" {...field} /></FormControl><FormMessage /></FormItem>)} />
                )}

                 <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                        <FormItem>
                             <FormLabel>Notes</FormLabel>
                             <FormControl>
                                <Textarea placeholder="Add any extra details, links, or notes here..." {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                 <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setIsOpen(false)}>Cancel</Button>
                    <Button type="submit">Save Activity</Button>
                </DialogFooter>
            </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
